/*  ========================================================================
    # Component - Application Form
    ========================================================================  */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../../hooks/useTranslation';
import UploadInput from './UploadInput';
import RecommendationInput from './RecommendationInput';

const ApplicationForm = ({
  variant,
  content,
  jobData,
  trainingJobs,
  applicantPrivacyLink,
  privacyLink,
  recommendationOptions
}) => {
  const __ = useTranslation();
  const pageParams = new URLSearchParams(window.location.search);
  const isInternshipVariant = variant === 'internship';
  const isTrainingVariant = variant === 'training';
  const isUnsolicitedVariant = variant === 'unsolicited';
  const isUsTrainingVariant = variant === 'us-training';
  const isGeneralVariant = !isInternshipVariant && !isTrainingVariant;

  // States
  const [fileSizes, setFileSizes] = useState({ cv: 0, application: 0, morefiles: 0 });
  const [resetKey, setResetKey] = useState(0);
  const [jobId, setJobId] = useState(jobData && jobData.lfdnr ? jobData.lfdnr : null);
  const [trainingJobID, setTrainingJobID] = useState(null); // Separate state to track selected training job ID for preselecting based on URL param
  const [jobArea, setJobArea] = useState(jobData && jobData.bereich ? jobData.bereich : null);
  const [internshipArea, setInternshipArea] = useState('');
  const [internshipType, setInternshipType] = useState('');

  // Form related states
  const [processingSubmission, setProcessingSubmission] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(null); // null = not submitted, true = success, false = error
  const [submissionError, setSubmissionError] = useState(null);

  const totalFilesSize = Object.values(fileSizes).reduce((a, b) => a + b, 0);

  const handleUploadChange = (fieldName, files) => {
    const totalSize = Array.isArray(files) ? files.reduce((sum, f) => sum + f.size, 0) : 0;
    setFileSizes(prev => ({ ...prev, [fieldName]: totalSize }));
  };

  const handleFormSubmit = async event => {
    event.preventDefault();

    const formData = new FormData(event.target);
    formData.append('action', 'pm_application_handling');
    formData.append('variant', variant);

    setProcessingSubmission(true);
    setSubmissionSuccess(null);
    setSubmissionError(null);

    try {
      const response = await fetch(ajax.url, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      if (result.success) {
        setSubmissionSuccess(true);
        setProcessingSubmission(false);

        event.target.reset();
        setFileSizes({ cv: 0, application: 0, morefiles: 0 });
        setResetKey(k => k + 1);
      } else {
        setSubmissionSuccess(false);
        setProcessingSubmission(false);

        if (result.data && result.data.message) {
          setSubmissionError(result.data.message);
        }
      }
    } catch (error) {
      setProcessingSubmission(false);
      setSubmissionSuccess(false);
      console.log(error);
    }
  };

  // Update job area when training job selection changes
  const handleTrainingJobChange = event => {
    const selectedJobId = event.target.value;
    const selectedJob = trainingJobs.find(job => job.jobCompareID === selectedJobId);

    if (selectedJob) {
      setJobArea(selectedJob.jobArea);
    } else {
      setJobArea('');
    }

    setTrainingJobID(selectedJob.jobCompareID);
    setJobId(selectedJob.jobID);
  };

  const getMoreFilesLabel = () => {
    if (isTrainingVariant) {
      return __('form-label-morefiles-upload-training');
    }
    if (isGeneralVariant && jobData['t_weitere-unterlagen']) {
      return jobData['t_weitere-unterlagen'];
    }
    if (isUnsolicitedVariant) {
      return __('form-label-morefiles-upload');
    }
    if (isInternshipVariant) {
      return __('form-label-morefiles-upload-internship');
    }
    return __('form-label-morefiles-upload');
  };

  const getApplicationFileLabel = () => {
    if (isTrainingVariant) {
      return __('form-label-application-upload-training');
    }

    if (isGeneralVariant && jobData.t_motivationsschreiben) {
      return jobData.t_motivationsschreiben;
    } else if (isGeneralVariant) {
      return __('form-label-application-upload');
    }

    return __('form-label-application-upload');
  };

  const getCvLabel = () => {
    if (!isGeneralVariant) return __('form-label-cv-upload-training');
    return jobData['t_lebenslauf-mit-foto'] || __('form-label-cv-upload');
  };

  // If variant is 'training', preselect job based on URL parameter
  useEffect(() => {
    if (variant === 'training' && trainingJobs.length > 0 && !trainingJobID) {
      const jobId = pageParams.get('job');
      const selectedJob = trainingJobs.find(job => job.jobCompareID === jobId);

      if (selectedJob) {
        setTrainingJobID(selectedJob.jobCompareID);
        setJobId(selectedJob.jobID);
        setJobArea(selectedJob.jobArea);
      }
    } else if (variant === 'us-training') {
      setJobId('0');
    }
  }, []);

  return (
    <div className="container">
      <div className="s-application-form__wrapper">
        {content && (
          <div className="s-application-form__content">
            <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        )}

        <div className="s-application-form__form">
          <form className="c-form" onSubmit={handleFormSubmit} encType="multipart/form-data">
            <fieldset className="c-form__item c-form__item--half">
              <label htmlFor="salutation">
                {jobData.t_anrede ? jobData.t_anrede : __('form-label-salutation')}
              </label>
              <select name="salutation" id="salutation">
                <option value="" disabled selected></option>
                <option value="M">{__('form-option-salutation-male')}</option>
                <option value="W">{__('form-option-salutation-female')}</option>
                <option value="D">{__('form-option-salutation-none')}</option>
              </select>
            </fieldset>
            {isUnsolicitedVariant && (
              <fieldset className="c-form__item c-form__item--half">
                <label htmlFor="jobName">{__('form-label-job-name')}</label>
                <input type="text" name="jobName" id="jobName" required />
              </fieldset>
            )}
            {isTrainingVariant && (
              <fieldset className="c-form__item c-form__item--half">
                <label htmlFor="trainingJob">{__('form-label-training-job')}</label>
                <select
                  name="trainingJob"
                  id=" "
                  required
                  value={trainingJobID}
                  onChange={handleTrainingJobChange}
                >
                  <option value="" disabled selected>
                    {__('form-option-training-job-placeholder')}
                  </option>
                  {trainingJobs.map((job, index) => (
                    <option key={index} value={job.jobCompareID}>
                      {job.title}
                    </option>
                  ))}
                </select>
                <span className="c-form__required-message" aria-hidden>
                  {__('form-required-message')}
                </span>
              </fieldset>
            )}
            {!isTrainingVariant && !isUnsolicitedVariant && (
              <div className="c-form__item c-form__item--half"></div>
            )}
            <fieldset className="c-form__item c-form__item--half">
              <label htmlFor="name">
                {jobData.t_name ? jobData.t_name : __('form-label-name')}
              </label>
              <input type="text" id="name" name="name" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>
            <fieldset className="c-form__item c-form__item--half">
              <label htmlFor="lastname">
                {jobData.t_nachname ? jobData.t_nachname : __('form-label-lastname')}
              </label>
              <input type="text" id="lastname" name="lastname" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>
            <fieldset className="c-form__item">
              <label htmlFor="email">
                {jobData['t_email-adresse'] ? jobData['t_email-adresse'] : __('form-label-email')}
              </label>
              <input type="email" id="email" name="email" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>
            <fieldset className="c-form__item">
              <label htmlFor="phone">
                {jobData.t_telefon ? jobData.t_telefon : __('form-label-phone')}
              </label>
              <input type="tel" id="phone" name="phone" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>
            <fieldset className="c-form__item c-form__item--half">
              <label htmlFor="zip">
                {jobData.t_postleitzahl ? jobData.t_postleitzahl : __('form-label-zip')}
              </label>
              <input type="text" id="zip" name="zip" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>
            <fieldset className="c-form__item c-form__item--half">
              <label htmlFor="location">
                {jobData.t_ort ? jobData.t_ort : __('form-label-location')}
              </label>
              <input type="text" id="location" name="location" autoComplete required />
              <span className="c-form__required-message" aria-hidden>
                {__('form-required-message')}
              </span>
            </fieldset>

            {isInternshipVariant && (
              <>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="street">{__('form-label-street')}</label>
                  <input type="text" id="street" name="street" autoComplete required />
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="birth">{__('form-label-birth')}</label>
                  <input
                    type="date"
                    max={new Date().toISOString().split('T')[0]}
                    id="birth"
                    name="birth"
                    required
                  />
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                <div className="c-form__item c-form__item--headline">
                  <label>{__('form-label-internship-data')}</label>
                </div>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="internshipArea">{__('form-label-internship-area')}</label>
                  <select
                    name="internshipArea"
                    id="internshipArea"
                    required
                    value={internshipArea}
                    onChange={e => setInternshipArea(e.target.value)}
                  >
                    <option value="" disabled>
                      {__('form-option-internship-area-placeholder')}
                    </option>
                    <option value="A">{__('form-option-internship-area-school')}</option>
                    <option value="S">{__('form-option-internship-area-university')}</option>
                  </select>
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="internshipType">{__('form-label-internship-type')}</label>
                  <select
                    name="internshipType"
                    id="internshipType"
                    required
                    value={internshipType}
                    onChange={e => setInternshipType(e.target.value)}
                  >
                    <option value="" disabled selected>
                      {__('form-option-internship-type-placeholder')}
                    </option>
                    <option value="S">{__('form-option-internship-type-required')}</option>
                    <option value="F">{__('form-option-internship-type-voluntary')}</option>
                  </select>
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="internshipFrom">{__('form-label-internship-from')}</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    id="internshipFrom"
                    name="internshipFrom"
                    required
                  />
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                <fieldset className="c-form__item c-form__item--half">
                  <label htmlFor="internshipTo">{__('form-label-internship-to')}</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    id="internshipTo"
                    name="internshipTo"
                    required
                  />
                  <span className="c-form__required-message" aria-hidden>
                    {__('form-required-message')}
                  </span>
                </fieldset>
                {internshipArea === 'A' && (
                  <>
                    {internshipType === 'S' && (
                      <fieldset className="c-form__item c-form__item--full">
                        <label htmlFor="internshipSchool">
                          {__('form-label-internship-school')}
                        </label>
                        <input type="text" id="internshipSchool" name="internshipSchool" required />
                        <span className="c-form__required-message" aria-hidden>
                          {__('form-required-message')}
                        </span>
                      </fieldset>
                    )}
                    <fieldset className="c-form__item c-form__item--full">
                      <label htmlFor="internshipJob">{__('form-label-internship-job')}</label>
                      <input type="text" id="internshipJob" name="internshipJob" required />
                      <span className="c-form__required-message" aria-hidden>
                        {__('form-required-message')}
                      </span>
                    </fieldset>
                  </>
                )}
              </>
            )}

            {isUsTrainingVariant && (
              <fieldset className="c-form__item c-form__item--half">
                <label htmlFor="yearOfGratuation">{__('form-label-year-of-graduation')}</label>
                <input
                  type="text"
                  id="yearOfGratuation"
                  name="yearOfGratuation"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength={4}
                  required
                />
                <span className="c-form__required-message" aria-hidden>
                  {__('form-required-message')}
                </span>
              </fieldset>
            )}

            {jobData.land_en === 'United States' && (
              <RecommendationInput options={recommendationOptions} />
            )}

            <div className="c-form__item c-form__item--headline">
              <label>
                {jobData['t_bewerbungsunterlagen-hochladen']
                  ? jobData['t_bewerbungsunterlagen-hochladen']
                  : __('form-label-application-data')}
              </label>
            </div>
            <UploadInput
              key={`cv-${resetKey}`}
              label={getCvLabel()}
              name="cv"
              required
              onFileChange={file => handleUploadChange('cv', file)}
              otherFilesSize={totalFilesSize - fileSizes.cv}
              customFileSizeError={
                jobData['t_bew-fehler-groe'] ? jobData['t_bew-fehler-groe'] : null
              }
            />
            <UploadInput
              key={`application-${resetKey}`}
              label={getApplicationFileLabel()}
              name="application"
              onFileChange={file => handleUploadChange('application', file)}
              otherFilesSize={totalFilesSize - fileSizes.application}
              customFileSizeError={
                jobData['t_bew-fehler-groe'] ? jobData['t_bew-fehler-groe'] : null
              }
            />
            <UploadInput
              key={`morefiles-${resetKey}`}
              label={getMoreFilesLabel()}
              name="morefiles"
              onFileChange={file => handleUploadChange('morefiles', file)}
              otherFilesSize={totalFilesSize - fileSizes.morefiles}
              customFileSizeError={
                jobData['t_bew-fehler-groe'] ? jobData['t_bew-fehler-groe'] : null
              }
            />

            {processingSubmission && (
              <fieldset className="c-form__item">
                <p className="c-form__message c-form__message--info">
                  {jobData['t_bew-hochladen']
                    ? jobData['t_bew-hochladen']
                    : __('form-application-message-uploading')}
                </p>
              </fieldset>
            )}

            {submissionSuccess === true && (
              <fieldset className="c-form__item">
                <p className="c-form__message c-form__message--success">
                  {jobData['t_bew-gesendet']
                    ? jobData['t_bew-gesendet']
                    : __('form-application-message-submission-success')}
                </p>
              </fieldset>
            )}

            {submissionSuccess === false && (
              <fieldset className="c-form__item">
                <p className="c-form__message c-form__message--error">
                  {__('form-application-message-submission-error')}
                </p>
              </fieldset>
            )}

            {submissionError && (
              <fieldset className="c-form__item">
                <p className="c-form__message c-form__message--error">{submissionError}</p>
              </fieldset>
            )}

            <fieldset className="c-form__item c-form__item--half">
              <div>
                <input type="checkbox" name="privacy" id="privacy" required />
                <label
                  htmlFor="privacy"
                  dangerouslySetInnerHTML={{
                    __html: jobData.t_datenschutz
                      ? jobData.t_datenschutz
                      : __('form-privacy-checkbox-text', { link: privacyLink.url })
                  }}
                ></label>
                <span className="c-form__required-message">{__('form-required-message')}</span>
              </div>
              <div>
                <input type="checkbox" name="applicantPrivacy" id="applicantPrivacy" />
                <label
                  htmlFor="applicantPrivacy"
                  dangerouslySetInnerHTML={{
                    __html: jobData.t_datenverarbeitung
                      ? jobData.t_datenverarbeitung
                      : __('form-legal-checkbox-text', { link: applicantPrivacyLink.url })
                  }}
                ></label>
              </div>
            </fieldset>
            <fieldset className="c-form__item c-form__item--submit">
              <button
                type="submit"
                className={`c-button c-button--primary ${processingSubmission ? 'c-button--loading' : ''}`}
                disabled={processingSubmission}
              >
                {processingSubmission
                  ? __('form-button-uploading')
                  : jobData['t_bewerbung-abschicken'] || __('form-button-submit-application')}
              </button>
            </fieldset>
            <fieldset className="c-form__item c-form__item--hidden">
              <input type="text" name="area" value={jobArea} hidden readOnly />
              <input type="text" name="jobId" value={jobId} hidden readOnly />
              <input
                type="text"
                name="language"
                value={document.documentElement.lang}
                hidden
                readOnly
              />
              <input
                type="text"
                name="utmSource"
                value={pageParams.get('utm_source')}
                hidden
                readOnly
              />
              <input
                type="text"
                name="utmMedium"
                value={pageParams.get('utm_medium')}
                hidden
                readOnly
              />
              <input
                type="text"
                name="utmCampaign"
                value={pageParams.get('utm_campaign')}
                hidden
                readOnly
              />
              <input
                type="text"
                name="utmTerm"
                value={pageParams.get('utm_term')}
                hidden
                readOnly
              />
              <input
                type="text"
                name="utmContent"
                value={pageParams.get('utm_content')}
                hidden
                readOnly
              />
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

ApplicationForm.propTypes = {
  variant: PropTypes.string,
  content: PropTypes.string,
  applicantPrivacyLink: PropTypes.string,
  privacyLink: PropTypes.string,
  jobData: PropTypes.array,
  trainingJobs: PropTypes.array,
  recommendationOptions: PropTypes.array
};

export default ApplicationForm;
